export type SimType = "ntc" | "ncell";

export interface Position {
  id: number;
  device_id: string;
  lat: number;
  lon: number;
  speed_kmh: number | null;
  heading_deg: number | null;
  altitude_m: number | null;
  alarm_bits: number | null;
  status_bits: number | null;
  /** Terminal odometer in kilometers. Null when the camera did not report it. */
  mileage_km?: number | null;
  recorded_at: string;
  received_at: string;
}

export interface Device {
  id: string;
  phone: string;
  serial_no: string;
  imei: string;
  sim_type: SimType;
  label: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  online: boolean;
  last_heartbeat_at: string | null;
  last_position: Position | null;
  live_channels: number[];
  /** Server minus terminal GPS clock. Positive when the camera is behind. */
  clock_skew_ms?: number | null;
}

export interface LiveGpsUpdate {
  device_id: string;
  phone?: string;
  serial_no?: string;
  label?: string | null;
  lat: number;
  lon: number;
  speed_kmh: number | null;
  heading_deg: number | null;
  altitude_m: number | null;
  alarm_bits: number | null;
  status_bits: number | null;
  /** Terminal odometer in kilometers. Null when the camera did not report it. */
  mileage_km?: number | null;
  /** False when the camera reported a packet with no satellite fix. */
  gps_fix?: boolean;
  recorded_at: string;
  received_at: string;
}

export interface ChannelStream {
  channel_no: number;
  mediamtx_path: string;
  hlsUrl: string;
  webrtcUrl: string;
  has_audio: boolean;
  live: boolean;
  /** MediaMTX control API lists this path as ready (a publisher is connected). */
  publishing?: boolean;
  waiting?: boolean;
  liveStart?: {
    phase?: string;
    ackResult?: number | null;
    jt1078Accepted?: boolean;
    at?: string;
  };
  /** Set by the dashboard when this live start failed. Playback tiles leave it unset. */
  liveFailed?: boolean;
  /** Specific reason when liveFailed is set. Playback tiles leave it unset. */
  failReason?: string;
  /** Dashboard-derived per-channel state. The API does not send this. */
  runState?: "idle" | "starting" | "publishing" | "playing" | "recovering" | "failed" | "offline" | "queued";
  /** 1-based place in the page start queue. */
  queuePosition?: number;
  /** Over the player cap: show a poster until the user clicks. */
  playHeld?: boolean;
}

export interface StreamInfo {
  hlsUrl: string | null;
  webrtcUrl: string | null;
  channels: ChannelStream[];
  targetDelaySec: number;
}

export interface AvResource {
  channel_no: number;
  start_at: string;
  end_at: string;
  file_size: number;
  av_type: number;
  stream_type: number;
  storage_type: number;
  alarm_flag: number;
}

export interface RecordingClip {
  id: string;
  start_at: string;
  end_at: string;
  duration_sec: number;
  channels: AvResource[];
}

export interface RecordingList {
  device_id: string;
  source: "device" | "cache" | "timeout" | "offline" | "unsupported" | "error";
  queried_at: string;
  from: string;
  to: string;
  clips: RecordingClip[];
  message?: string;
  sent_9205?: boolean;
  trace?: Record<string, unknown>;
}

export interface RecordingStreamInfo {
  channels: ChannelStream[];
  targetDelaySec: number;
  start_at: string;
  end_at: string;
  pipeline?: Record<string, unknown>;
}

export interface RecordingStartResult {
  ok: boolean;
  mode?: string;
  sessionId?: string;
  /** Fresh /pb/chN/{hlsGen} after a relocate seek. Absent on the initial slice. */
  hlsGen?: string | null;
  channels?: number[];
  startAt?: string;
  endAt?: string;
  clipEndAt?: string;
  files?: Array<{
    channel: number;
    startAt: string;
    endAt: string;
    mediaType?: number;
    streamType?: number;
    storageType?: number;
  }>;
  hlsResults?: Array<{
    channel: number;
    ready: boolean;
    pbPath?: string;
    packets?: number;
    hlsHasSegments?: boolean;
  }>;
  warming?: boolean;
  /** Next slice was opened on the current HLS path while playback continued. */
  extended?: boolean;
  message?: string;
}

export interface DeviceInput {
  phone: string;
  serial_no: string;
  imei: string;
  sim_type: SimType;
  label?: string | null;
  is_active?: boolean;
}

export const DEV_LOG_STREAMS = ["api", "tcp", "udp", "mediamtx", "rtsp"] as const;
export type DevLogStreamId = (typeof DEV_LOG_STREAMS)[number];

export interface DevLogEvent {
  ts: string;
  stream: DevLogStreamId;
  level: string;
  service: string;
  msg: string;
  data?: Record<string, unknown>;
}

export const apiBase = "";

let csrfToken = "";
let sessionToken = "";
const SESSION_TOKEN_KEY = "dc_master_session";

export function setCsrf(token: string): void {
  csrfToken = token;
  try {
    if (token) sessionStorage.setItem("dc_dev_csrf", token);
    else sessionStorage.removeItem("dc_dev_csrf");
  } catch {
    /* ignore */
  }
}

export function getCsrf(): string {
  if (csrfToken) return csrfToken;
  try {
    const stored = sessionStorage.getItem("dc_dev_csrf") || "";
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|; )dc_dev_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1] ?? "") : "";
}

/** Same login session as the HttpOnly cookie. Kept for requests the cookie does not ride on. */
export function setSessionToken(token: string): void {
  sessionToken = token;
  try {
    if (token) sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    else sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getSessionToken(): string {
  if (sessionToken) return sessionToken;
  try {
    const stored = sessionStorage.getItem(SESSION_TOKEN_KEY) || "";
    if (stored) sessionToken = stored;
    return stored;
  } catch {
    return "";
  }
}

export function clearMasterSession(): void {
  setSessionToken("");
  setCsrf("");
}

/** Master WebSocket URL with the logged-in session, for browsers that cannot set socket headers. */
export function masterSocketUrl(path: string, explicit?: string): string {
  const proto = typeof window !== "undefined" && window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = typeof window !== "undefined" ? window.location.host : "localhost";
  const url = new URL(explicit || path, `${proto}//${host}`);
  const token = getSessionToken();
  if (token && !url.searchParams.has("session")) url.searchParams.set("session", token);
  return url.toString();
}

/** Keep media URLs on the page origin so HTTPS dashboards never load http:// HLS. */
export function playbackUrl(url: string, origin = typeof window !== "undefined" ? window.location.href : ""): string {
  if (!url) return url;
  if (!origin) return url.startsWith("http://") ? url.replace(/^http:\/\//i, "https://") : url;
  try {
    const page = new URL(origin);
    const resolved = new URL(url, page);
    if (resolved.hostname === page.hostname) {
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    }
    if (page.protocol === "https:" && resolved.protocol === "http:") {
      resolved.protocol = "https:";
    }
    return resolved.toString();
  } catch {
    return url;
  }
}

/** HTML gateway pages are not partner JSON. Only live-media calls use media wording. */
export function upstreamErrorMessage(path: string, status: number, text: string): string | null {
  const media = /\/(?:stream|hls|webrtc|playback)(?:\/|$|\?)|\/recordings\/stream/.test(path);
  const developer = path.startsWith("/api/developers");
  if (status === 504 || /gateway time-?out/i.test(text)) {
    if (media) return "Playback start timed out waiting for the camera. Tap Retry.";
    if (developer) return "Developer API timed out. Refresh and try again.";
    return `Request timed out (HTTP ${status}).`;
  }
  if (status === 502 || /bad gateway/i.test(text)) {
    if (developer) return "Developer API is briefly unavailable. Refresh and try again.";
    if (media) return "Media gateway briefly unavailable. Tap Retry.";
    return `Upstream unavailable (HTTP ${status}).`;
  }
  return null;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const method = (init?.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const csrf = getCsrf();
    if (csrf && !headers.has("X-CSRF-Token")) headers.set("X-CSRF-Token", csrf);
  }
  const session = getSessionToken();
  if (session && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${session}`);
  if (method !== "GET" && method !== "HEAD" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      const scrubbed = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160);
      const mapped = upstreamErrorMessage(path, res.status, text);
      let message = mapped ?? (scrubbed || `HTTP ${res.status}`);
      if (!mapped && /<html/i.test(text)) {
        message = `Server error (HTTP ${res.status}). Tap Retry.`;
      }
      body = { message };
    }
  }
  if (!res.ok) {
    const err = body as {
      error?: string;
      message?: string;
      details?: { formErrors?: string[]; fieldErrors?: Record<string, string[] | undefined> };
    } | null;
    const fieldMsgs = err?.details?.fieldErrors
      ? Object.entries(err.details.fieldErrors)
          .flatMap(([field, msgs]) => (msgs ?? []).map((m) => `${field}: ${m}`))
          .join("; ")
      : "";
    const formMsgs = err?.details?.formErrors?.filter(Boolean).join("; ") ?? "";
    const detailText = [formMsgs, fieldMsgs].filter(Boolean).join("; ");
    const human =
      err?.message ||
      detailText ||
      (err?.error === "validation_error" ? "Validation failed." : undefined) ||
      err?.error ||
      `HTTP ${res.status}`;
    const error = new Error(human) as Error & { status?: number; code?: string };
    error.status = res.status;
    error.code = err?.error;
    // Prefer API error code for client mapping; keep human message when provided for setup/disabled.
    if (err?.error === "setup_required" || err?.error === "account_disabled") {
      error.message = err.error;
    }
    throw error;
  }
  return body as T;
}
